import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as authClient from './auth-client';
import { AuthApiError, UnauthorizedError } from './auth-client';
import type { User, TelegramAuthPayload, StartResult } from './auth-client';
import { trackCompleteRegistration, setGaUserId } from './tracking';
import { setInternalViewer } from './internal-viewer';
import { phIdentify, phReset, phSetInternal } from './posthog';
import { track } from './funnel';
import { ApiError, setCheckoutToken } from './api';
import { uuid } from './uuid';
import { clearPendingBotLogin } from './telegram-bot-login';
import { clearAuthHint, readAuthHint, writeAuthHint } from './auth-hint';
import { isPaidUser } from './user-display';
import { isQaSinkEmail } from '@tenders/shared';

/**
 * A sign-up on a QA sink address goes quiet BEFORE its conversion fires.
 *
 * The staff flag normally arrives with the session (`/auth/me`), but
 * `trackCompleteRegistration` runs the moment the sign-up call answers — one
 * step earlier — and a fresh QA account is not flagged `internal` yet anyway
 * (that is done by hand afterwards). So a QA sign-up used to hand Meta a
 * browser CompleteRegistration and GA4 a `sign_up`. The server leg skips the
 * same addresses (`MetaConversionsService`); both read the shared list.
 */
function muteQaSignup(email: string): void {
  if (!isQaSinkEmail(email)) return;
  setInternalViewer(true);
  phSetInternal(true);
}

export type { User };

export type AuthStatus = 'loading' | 'guest' | 'authed';

/** `/auth/me` resilience: how many times to try on a TRANSIENT (5xx/network)
 *  failure before giving up, and the linear back-off between tries (attempt×base
 *  → ~300ms, 600ms). A clean 401 does not retry — it's a definitive answer. */
const ME_MAX_ATTEMPTS = 3;
const ME_RETRY_BACKOFF_MS = 300;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  login: (params: { email: string; password: string }) => Promise<void>;
  register: (params: {
    email: string;
    password: string;
    name?: string;
    tin?: string;
  }) => Promise<void>;
  /**
   * `POST /auth/start` — soft-signup entry point. On `signed_in` this already
   * rehydrates `user`/`status` (the session cookie is set server-side, so a
   * `refresh()` here is what actually reads it back) — the caller only needs
   * to branch on `status` and navigate.
   */
  start: (params: {
    email: string;
    name?: string;
    phone?: string;
    locale?: string;
    /**
     * Checkout only. Creates the account server-side but leaves THIS BROWSER a
     * guest: no cookie, no `refresh()`, no onboarding, and `accessToken` comes
     * back on the result for `setCheckoutToken`. See `adoptCheckoutSession`.
     */
    silent?: boolean;
  }) => Promise<StartResult>;
  /**
   * Trade the checkout token for a real session — the last step of a guest
   * checkout, run only once the money has moved (a charge that succeeded, or a
   * hand-off to a hosted provider page the buyer is about to land on).
   */
  adoptCheckoutSession: () => Promise<void>;
  /** Ask for an email login code. Resolves for any address — see auth-client. */
  requestOtp: (params: { email: string; locale?: string; intent?: AuthIntent }) => Promise<void>;
  verifyOtp: (params: {
    email: string;
    code: string;
    name?: string;
    phone?: string;
    locale?: string;
    intent?: AuthIntent;
  }) => Promise<void>;
  loginWithTelegram: (payload: TelegramAuthPayload, intent?: AuthIntent) => Promise<void>;
  /**
   * The browser end of «Войти через Telegram» through the bot. The server has
   * ALREADY done the sign-in (session cookie set, or Telegram linked to the
   * session that polled); this only does what `loginWithTelegram` does after
   * its own request — the Pixel leg of CompleteRegistration for a new account,
   * then re-reading `/auth/me`.
   */
  completeTelegramBotLogin: (result: {
    isNew: boolean;
    linked: boolean;
    metaEventId?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/*
 * Auth failures, recorded centrally.
 *
 * register.tsx has 8 `setError` sites and login.tsx has 10, and NONE of them
 * emitted anything — so a person who bounced off the signup form and a person
 * the signup form REJECTED wrote the identical row: none. That is exactly the
 * confusion the checkout ladder was built to remove, sitting one step earlier
 * in the same funnel and affecting far more people (729 registrations against
 * 74 checkouts).
 *
 * Emitted here rather than at the 18 call sites: one place cannot drift, and
 * every path already funnels through these catches.
 *
 * The REASON is the server's error code where there is one, never the message —
 * messages are localised prose that would mint a new vocabulary value per
 * translation, and can carry back whatever the user typed.
 */
function authFailure(
  event: 'login_failed' | 'register_failed',
  method: string,
  err: unknown,
): void {
  track(event, { method, reason: failureReason(err) });
}

/**
 * Which side of the door the caller was on.
 *
 * `login` and `register` share EVERY passwordless path — the same email-code
 * exchange and the same Telegram widget create an account or resume one, and
 * the provider cannot tell which from the response. So the intent has to come
 * from the route: `/login` or `/register`.
 *
 * Without it `register_failed` was unreachable in practice. It fired only from
 * the password `register()` call, which nothing on the live site uses, so it
 * held ZERO rows from the day it shipped while every rejected signup was filed
 * under `login_failed` — the exact "bounced vs rejected" confusion the docblock
 * above says this instrumentation exists to end, reappearing as "rejected at
 * signup vs rejected at login".
 *
 * Defaults to `login` at every call site, so an unannotated caller keeps the
 * previous behaviour instead of inventing signups.
 */
export type AuthIntent = 'login' | 'register';

export const failureEvent = (intent: AuthIntent | undefined) =>
  intent === 'register' ? ('register_failed' as const) : ('login_failed' as const);

/**
 * Classify an auth rejection.
 *
 * ⚠️ `auth-client` throws its OWN error classes — `AuthApiError` (carrying the
 * API's machine-readable `code`) and `UnauthorizedError` — NOT the `ApiError`
 * used elsewhere in the app. An earlier version checked only `ApiError`, so
 * EVERY auth failure fell through to `'network'`: a clean 401 on a wrong SMS
 * code was recorded as a connectivity problem. It reached production, and the
 * unit tests missed it because they threw the wrong class — exercising a path
 * the real code never takes.
 *
 * `network` must therefore mean ONLY a request that never got an answer. A
 * refused credential and a dead connection are different problems with
 * different fixes, and collapsing them makes the field worse than absent.
 */
export function failureReason(err: unknown): string {
  if (err instanceof AuthApiError) return err.code ?? 'api_error';
  if (err instanceof UnauthorizedError) return 'unauthorized';
  // The client throws a bare `Error('HTTP <status>')` when the body carried no
  // error envelope — still a server answer, not a network failure.
  const httpStatus = err instanceof Error ? /^HTTP (\d{3})$/.exec(err.message) : null;
  if (httpStatus) return `http_${httpStatus[1]}`;
  // Kept for the generic client, which no auth path uses today — a future
  // caller would otherwise regress silently.
  if (err instanceof ApiError) return err.code ?? 'api_error';
  return 'network';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  // Drop every cached query when the signed-in identity changes. React Query keys
  // like ['saved-searches'] are NOT user-scoped, so without this a previous
  // account's data (e.g. their saved searches) lingers after an account switch —
  // deleting one then 403s "Not your saved search" because the cached row belongs
  // to the old user. Called on every login/register/telegram/logout transition.
  const resetCache = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async () => {
    // `me()` resolves the user on 200 and returns `null` on a CLEAN 401 (a
    // genuine "not signed in"). It only THROWS on a transient failure — a 5xx or
    // a network error. Those two must be handled differently: a 401 is the
    // server telling us there is no session; a 500 is the server failing to
    // answer, which is NOT evidence the session is gone.
    //
    // The old code caught both and dropped straight to `guest`, so any API blip
    // logged the user out — most visibly under prerender load, where the pool
    // saturated and `/auth/me` 500'd (see docker-compose connection_limit note).
    // That surfaced as "Server Error right after login" and "kicked out after a
    // couple hours". So: retry a transient failure a few times, and if it still
    // won't answer, PRESERVE an already-authed session rather than forcing a
    // false logout.
    for (let attempt = 1; attempt <= ME_MAX_ATTEMPTS; attempt++) {
      try {
        const me = await authClient.me();
        if (me) {
          setUser(me.user);
          setStatus('authed');
          // Painting hint for the next hard load's menu (`auth-hint.ts`) — it
          // authorizes nothing. `myTenders` is the sidebar's to know (it needs
          // the saved-search list), so keep whatever it last stored.
          writeAuthHint({
            paid: isPaidUser(me.user),
            myTenders: readAuthHint()?.myTenders ?? false,
          });
          // ⚠️ NOTHING is pushed to the Meta Pixel here, and there is no way to.
          // A `setMetaUserData(me.user)` sat on this line for one day (2026-09-26)
          // and called `fbq('set','userData', …)` to attach hashed em/ph/fn/ln
          // after `init`. Measured against production the same day: fbevents.js
          // rejects the call with an "invalid pixel_id" error on every page load
          // and `fbq.getState().pixels[0].userData` never changed. The pixel's
          // only identity is the `external_id` on the single `init` in
          // index.html; `em`/`ph`/`fn`/`ln` reach Meta on the CAPI leg of the
          // server-authored events. See the note atop `lib/tracking.ts`.
        } else {
          setUser(null);
          setStatus('guest');
          // A clean 401 is the server's answer — e.g. a logout on another
          // device. The transient-error branch below leaves the hint alone.
          clearAuthHint();
        }
        setError(null);
        return;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        if (attempt < ME_MAX_ATTEMPTS) {
          await sleep(ME_RETRY_BACKOFF_MS * attempt);
          continue;
        }
        // Retries exhausted on a transient error. Keep an already-authed user
        // signed in; only a first-ever load (still 'loading') falls back to
        // 'guest' — and even then the httpOnly cookie survives, so the next
        // refresh recovers the session once the API is healthy again.
        setStatus((prev) => (prev === 'authed' ? 'authed' : 'guest'));
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Link the PostHog identity to the same person on auth transitions: identify
  // on login/register (so anonymous replay/events attribute to the user), reset
  // on logout. Keyed on the same user id the DIY funnel + Meta use.
  const phUserId = useRef<string | null>(null);
  useEffect(() => {
    // Staff / test accounts: silence Meta Pixel + GA (tracking.ts) and PostHog
    // for this browser. Deliberately NOT cleared on logout — the tester keeps
    // testing logged-out on the same browser; a later login by a real (non-
    // internal) user clears it.
    if (user) {
      // `|| isQaSinkEmail`: a fresh QA account is not flagged yet, and without
      // this the first session read would switch its analytics back on.
      const internal = Boolean(user.internal) || isQaSinkEmail(user.email);
      setInternalViewer(internal);
      phSetInternal(internal);
    }
    if (user && user.id !== phUserId.current) {
      /*
       * `$set` writes PERSON properties, not just event properties.
       *
       * Nothing but `email` was ever set on a PostHog person, so no insight,
       * cohort, retention curve or replay filter could be split by paid versus
       * free — the one dimension every retention question starts with. The
       * mirrored activation spine is only usable once this exists.
       *
       * `via` matters as much as the plan: a team seat is entitlement without a
       * purchase, and counting it as a paying customer would overstate
       * conversion and understate ARPU at the same time.
       */
      const sub = user.subscription;
      phIdentify(user.id, {
        email: user.email,
        $set: {
          plan: sub?.plan ?? 'free',
          isPaying: Boolean(sub && !sub.expired),
          planVia: sub?.via ?? null,
          autoRenew: sub?.autoRenew ?? null,
        },
      });
      // GA4 identity — see setGaUserId. Device-scoped until now, so one person
      // across two devices was two users and no report could join them.
      setGaUserId(user.id);
      phUserId.current = user.id;
    } else if (!user && phUserId.current) {
      phReset();
      // `null`, never '' — Google documents a blank id as causing permanent
      // data loss.
      setGaUserId(null);
      phUserId.current = null;
    }
  }, [user]);

  // The identity the app is rendering for, readable from callbacks without
  // re-creating them on every auth change.
  const userRef = useRef<User | null>(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  /**
   * Adopt a session the API just created. Two very different situations land
   * here. An account SWITCH (a member signing in as someone else) must drop the
   * whole cache — see `resetCache`. A GUEST becoming a member has no previous
   * identity whose rows could leak, and clearing the cache there does real
   * damage: every component rendered from a query — the /plans cards and the
   * checkout sheet open above them, with the card the buyer has just typed —
   * remounts against an empty cache the instant the account is born (guest
   * checkout, MR !570; the same sheet reaches `login`/`verifyOtp` for an
   * address that needs a password or a code). So a guest keeps the cache
   * mounted and refetches in place, which is also what replaces the
   * guest-scoped answers (locked teasers, anonymous payment methods) with the
   * member's.
   *
   * Every completed sign-in also forgets a «Войти через Telegram» this browser
   * left pending (`uzt_tgl_wait`, security review 18.10): it was tapped before
   * this session existed, and must not be collected into it later. The server
   * refuses that anyway (409 `telegram_link_mismatch`); this keeps the app
   * root from even asking.
   */
  const adoptSession = useCallback(async () => {
    clearPendingBotLogin();
    const switching = userRef.current !== null;
    if (switching) resetCache();
    await refresh();
    if (!switching) void queryClient.invalidateQueries();
  }, [refresh, resetCache, queryClient]);

  /**
   * The end of a guest checkout: the browser becomes a member.
   *
   * `authClient.adoptSession()` sends the checkout token as a Bearer and gets
   * the session cookie written for it; `adoptSession()` above then does what
   * every other guest→member transition does — refresh `/auth/me` and refetch
   * in place, never `clear()`, because the sheet and the /plans cards this is
   * called from are rendered out of that very cache.
   *
   * The token is dropped only on SUCCESS. A failure leaves it held, so the
   * buyer keeps whatever access the payment bought them for the rest of this
   * page's life and a retry is possible; it also means nothing is lost if the
   * adopt call is the one thing that goes wrong after a successful charge.
   */
  const adoptCheckoutSession = useCallback(async () => {
    await authClient.adoptSession();
    await adoptSession();
    setCheckoutToken(null);
  }, [adoptSession]);

  const login = useCallback(
    async (params: { email: string; password: string }) => {
      setError(null);
      try {
        await authClient.login(params);
        await adoptSession();
      } catch (err) {
        authFailure('login_failed', 'password', err);
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      }
    },
    [adoptSession],
  );

  const register = useCallback(
    async (params: { email: string; password: string; name?: string; tin?: string }) => {
      setError(null);
      try {
        // Shared id so the browser Pixel CompleteRegistration and the server
        // CAPI event collapse into one conversion in Meta.
        const metaEventId = uuid();
        await authClient.register({ ...params, metaEventId });
        muteQaSignup(params.email);
        trackCompleteRegistration(metaEventId);
        await adoptSession();
      } catch (err) {
        authFailure('register_failed', 'password', err);
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      }
    },
    [adoptSession],
  );

  /**
   * `POST /auth/start` — the login page's step-1 handler. A new/soft address
   * gets an account AND a session in one round trip (`signed_in`); a verified
   * or password address is routed to whichever proof it needs. Only the
   * `signed_in` branch actually created a session, so only it resets the
   * query cache and re-reads `/auth/me` — a `code_required`/`password_required`
   * result left `user`/`status` untouched and must not clear anyone's cache.
   *
   * This is also where CompleteRegistration fires for a soft signup: the
   * account is born here, and gating the conversion on the later email verify
   * lost a third of it (only 54–64% of accounts verify since soft-signup
   * shipped). `isNew`, not `status`, is the gate — a returning soft account
   * answers `signed_in` on every visit.
   */
  const start = useCallback(
    async (params: {
      email: string;
      name?: string;
      phone?: string;
      locale?: string;
      silent?: boolean;
    }) => {
      setError(null);
      try {
        // Shared id so the browser Pixel CompleteRegistration and the server
        // CAPI event collapse into one conversion in Meta — same contract as
        // `register` above.
        const metaEventId = uuid();
        const result = await authClient.start(params.email, {
          name: params.name,
          phone: params.phone,
          locale: params.locale,
          metaEventId,
          silent: params.silent,
        });
        muteQaSignup(params.email);
        if (result.isNew) trackCompleteRegistration(metaEventId);
        /*
         * ⚠️ A SILENT `signed_in` adopts nothing.
         *
         * The account exists server-side — the ad account and the funnel see it
         * (the conversion above fires either way) — but the browser stays a
         * guest until the payment succeeds. No cookie was set, so `refresh()`
         * would only re-read the same anonymous `/auth/me` and, worse, would
         * flip `status` for any component that DOES see a session: onboarding
         * over the payment sheet, a frozen email field, «вы вошли» before a
         * single сум moved. The caller keeps `result.accessToken` instead, and
         * calls `adoptCheckoutSession()` when the money has moved.
         */
        if (result.status === 'signed_in' && !params.silent) await adoptSession();
        return result;
      } catch (err) {
        authFailure('login_failed', 'start', err);
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      }
    },
    [adoptSession],
  );

  /**
   * Redeem an email login code. Both signup and login — a first redemption
   * creates the account server-side, so the Pixel CompleteRegistration fires
   * only when `isNew`, mirroring the Telegram path. Firing it unconditionally
   * would count every returning login as a conversion.
   */
  /**
   * Ask for an email code.
   *
   * This was passed straight through from `authClient` — no catch, no event —
   * so every rejection AT THIS STEP wrote nothing anywhere: rate limits, a
   * malformed address, the server being down. It is the first thing a new user
   * does, and it was the one auth step with no instrumentation at all.
   *
   * `authClient.requestOtp` resolves for any address on purpose (it must not
   * leak whether an account exists), so what lands here is a genuine failure,
   * not "unknown email".
   */
  const requestOtp = useCallback(
    async ({ intent, ...params }: { email: string; locale?: string; intent?: AuthIntent }) => {
      setError(null);
      try {
        await authClient.requestOtp(params);
      } catch (err) {
        authFailure(failureEvent(intent), 'otp_request', err);
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      }
    },
    [],
  );

  const verifyOtp = useCallback(
    async ({
      intent,
      ...params
    }: {
      email: string;
      code: string;
      name?: string;
      phone?: string;
      locale?: string;
      intent?: AuthIntent;
    }) => {
      setError(null);
      try {
        const metaEventId = uuid();
        const result = await authClient.verifyOtp({ ...params, metaEventId });
        muteQaSignup(params.email);
        // Gated on `isNew` — account CREATION — mirroring the server-side CAPI
        // gate in AuthController.verifyOtp. Post soft-signup `isNew` is usually
        // already false here because the account was created at `/auth/start`,
        // which is where the event fires for it; this branch covers the direct
        // otp/verify path that creates the account itself. One account, one
        // conversion, from whichever door it came through.
        //
        // ⚠️ Not `firstVerification`: since soft-signup only 54–64% of accounts
        // ever verify, so that gate drops a third of signups from the event the
        // ad account optimises on.
        if (result.isNew) trackCompleteRegistration(metaEventId);
        await adoptSession();
      } catch (err) {
        // The live email path: a wrong or expired code lands here. It is both a
        // login and a signup, so which event it is comes from the ROUTE the
        // caller was on; `method` still tells the paths apart within each.
        authFailure(failureEvent(intent), 'otp', err);
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      }
    },
    [adoptSession],
  );

  const loginWithTelegram = useCallback(
    async (payload: TelegramAuthPayload, intent?: AuthIntent) => {
      setError(null);
      try {
        // Shared id so the browser Pixel CompleteRegistration and the server-side
        // CAPI event collapse into one conversion (mirrors the email register flow).
        const metaEventId = uuid();
        const result = await authClient.loginWithTelegram(payload, metaEventId);
        // Only a first-ever Telegram sign-in is a registration — fire the Pixel
        // event just for new accounts so returning logins don't over-count.
        if (result.isNew) trackCompleteRegistration(metaEventId);
        await adoptSession();
      } catch (err) {
        // `telegram_account_taken` lands here — a known dead end where the same
        // person has both an email and a Telegram account and cannot link them.
        // On /register that dead end IS a failed signup, and filing it as a
        // login failure hid the collision behind the wrong funnel.
        authFailure(failureEvent(intent), 'telegram', err);
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      }
    },
    [adoptSession],
  );

  const completeTelegramBotLogin = useCallback(
    async (result: { isNew: boolean; linked: boolean; metaEventId?: string }) => {
      setError(null);
      // Same gate as `loginWithTelegram`: only a first-ever sign-in is a
      // registration, and the id is the one the server's CAPI leg carries.
      if (result.isNew && result.metaEventId) trackCompleteRegistration(result.metaEventId);
      // A LINK kept the session this browser already had — re-read it, never
      // `adoptSession`, whose account-switch branch would clear the whole query
      // cache under a page that has not changed hands. A login adopts.
      if (result.linked) await refresh();
      else await adoptSession();
    },
    [adoptSession, refresh],
  );

  const logout = useCallback(async () => {
    setError(null);
    // A «Войти через Telegram» tapped here in the last fifteen minutes goes with
    // the session (security review, 18.10): on a shared computer, a Start
    // pressed later must not sign in — or attach to — whoever comes next. The
    // server drops its half (the httpOnly nonce) in the same logout.
    clearPendingBotLogin();
    // Await the server logout — the session cookie is httpOnly and only the
    // server can delete it; otherwise `me()` re-authenticates on the next load.
    await authClient.logout();
    clearAuthHint();
    setUser(null);
    setStatus('guest');
    // Drop the prior user's cached queries so the next account starts clean.
    resetCache();
  }, [resetCache]);

  return (
    <AuthContext.Provider
      value={{
        user,
        status,
        login,
        register,
        start,
        adoptCheckoutSession,
        requestOtp,
        verifyOtp,
        loginWithTelegram,
        completeTelegramBotLogin,
        logout,
        refresh,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Non-throwing auth read for widely-reused leaf components (e.g. the card's ☆
 *  SaveStar) that may render in a tree without AuthProvider — notably unit tests.
 *  Returns null when there's no provider; callers treat that as "not signed in". */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext);
}
